package fr.frux.feature.harvests

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Place
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import fr.frux.app.resources.Res
import fr.frux.app.resources.harvests_place_clear_description
import fr.frux.app.resources.harvests_place_clear_query_description
import fr.frux.app.resources.harvests_place_field_placeholder
import fr.frux.app.resources.harvests_place_selected
import fr.frux.core.designsystem.components.FruxCard
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.shared.domain.model.Place
import org.jetbrains.compose.resources.stringResource

/**
 * Choix du lieu autour duquel lister les récoltes (#412). Réutilise la recherche de lieux du
 * géocodeur (ADR 0024) exposée par `PlaceRepository`, sans rien emprunter à la carte : celle-ci
 * cherche aussi des espèces et des arbres, et son composant vit dans `feature-map`, sous cet
 * écran dans l'ordre des dépendances.
 *
 * Un lieu choisi remplace le champ par sa mention, seule façon de montrer que la liste ne parle
 * plus d'« autour de toi » — sans quoi une liste vide à Paris serait illisible depuis Montreuil.
 */
@Composable
internal fun HarvestsPlaceField(
    query: String,
    results: List<Place>,
    selectedPlace: Place?,
    onQueryChange: (String) -> Unit,
    onPlaceClick: (Place) -> Unit,
    onClearPlace: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val focusManager = LocalFocusManager.current

    Column(modifier = modifier) {
        if (selectedPlace != null) {
            SelectedPlaceRow(place = selectedPlace, onClear = onClearPlace)
            return@Column
        }

        val fieldLabel = stringResource(Res.string.harvests_place_field_placeholder)
        Surface(
            shape = RoundedCornerShape(50),
            color = MaterialTheme.colorScheme.surfaceVariant,
            modifier = Modifier.fillMaxWidth(),
        ) {
            Row(
                modifier = Modifier.padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    imageVector = Icons.Outlined.Search,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(modifier = Modifier.width(FruxSpacing.sm))
                Box(modifier = Modifier.weight(1f)) {
                    if (query.isEmpty()) {
                        Text(
                            text = fieldLabel,
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                    BasicTextField(
                        value = query,
                        onValueChange = onQueryChange,
                        singleLine = true,
                        textStyle =
                            MaterialTheme.typography.bodyLarge.copy(
                                color = MaterialTheme.colorScheme.onSurface,
                            ),
                        cursorBrush = SolidColor(MaterialTheme.colorScheme.primary),
                        keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
                        keyboardActions = KeyboardActions(onSearch = { focusManager.clearFocus() }),
                        // Le texte d'invite est un Text frère, pas le libellé du champ : sans ceci
                        // le champ arrive vide dans l'arbre d'accessibilité (même motif que la
                        // pastille de recherche de la carte).
                        modifier =
                            Modifier
                                .fillMaxWidth()
                                .semantics { contentDescription = fieldLabel },
                    )
                }
                if (query.isNotEmpty()) {
                    FruxIconButton(
                        icon = Icons.Outlined.Close,
                        contentDescription = stringResource(Res.string.harvests_place_clear_query_description),
                        onClick = { onQueryChange("") },
                        tint = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }

        if (results.isNotEmpty()) {
            Spacer(modifier = Modifier.height(FruxSpacing.xs))
            FruxCard(modifier = Modifier.fillMaxWidth()) {
                results.forEach { place ->
                    PlaceRow(
                        place = place,
                        onClick = {
                            // Même leçon que la carte (#413) : choisir un résultat termine la
                            // saisie, sinon le clavier reste ouvert sur la liste qu'on vient de
                            // demander.
                            focusManager.clearFocus()
                            onPlaceClick(place)
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun PlaceRow(
    place: Place,
    onClick: () -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier =
            Modifier
                .fillMaxWidth()
                .clickable(onClick = onClick)
                .padding(vertical = FruxSpacing.sm),
    ) {
        Icon(
            imageVector = Icons.Outlined.Place,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Spacer(modifier = Modifier.width(FruxSpacing.sm))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = place.label,
                style = MaterialTheme.typography.bodyLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            place.detail?.let { detail ->
                Text(
                    text = detail,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
private fun SelectedPlaceRow(
    place: Place,
    onClear: () -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier =
            Modifier
                .fillMaxWidth()
                .background(MaterialTheme.colorScheme.secondaryContainer, RoundedCornerShape(50))
                .padding(start = FruxSpacing.md, end = FruxSpacing.xs, top = 4.dp, bottom = 4.dp),
    ) {
        Icon(
            imageVector = Icons.Outlined.Place,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSecondaryContainer,
        )
        Spacer(modifier = Modifier.width(FruxSpacing.sm))
        Text(
            text = stringResource(Res.string.harvests_place_selected, place.label),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSecondaryContainer,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f),
        )
        FruxIconButton(
            icon = Icons.Outlined.Close,
            contentDescription = stringResource(Res.string.harvests_place_clear_description),
            onClick = onClear,
            tint = MaterialTheme.colorScheme.onSecondaryContainer,
        )
    }
}
